/**
 * <EncounterWorkspace>
 *
 * Shared shell for the two entry points of the Progress Note / scribe
 * workspace:
 *   - `/call/:encounterId` (page mode) — full-screen telehealth experience.
 *     CallPage already resolves practitioner/org/project upstream and feeds
 *     them in as explicit props.
 *   - Patient chart "New Encounter" side panel (side-panel mode) — wired in
 *     the panel-registry at PatientPage.tsx. The caller only knows the
 *     patient, so the workspace resolves the chart visit context itself
 *     (the legacy `<ProgressNoteNewEncounter>` did this inline; we fold the
 *     same logic in here behind a `patient`-prop branch).
 *
 * Behavior
 *  - Both modes mount `<ProgressNoteEdit>` directly as the main content.
 *    The form renders empty/draft on mount and is populated either by the
 *    AI scribe finalize step (via `<ScribeFormBridgeContext>.populateFromAi`)
 *    or by manual doctor typing. This matches the provider-portal mockup
 *    at `ui-designs/.../ChartPage.tsx:3674-4080` where the Progress Note
 *    IS the main content during a call (no placeholder, no "open the chart"
 *    indirection).
 *  - Page mode reads its visit context from explicit props (CallPage owns
 *    the resolve). Side-panel mode resolves the chart visit context inline
 *    via `resolveChartVisitContext` + prefetches the patient coverage cache
 *    before rendering, mirroring what `<ProgressNoteNewEncounter>` used to
 *    do.
 *  - Both modes mount `<ZoomPanel>` as a sibling, which renders only when
 *    `useCallSession().isJoined` is true and is a no-op otherwise.
 *  - Page mode does NOT wrap in its own `<SoapNotePlanProvider>` — CallPage
 *    already provides one upstream so the chart's "Add to plan" call-sites
 *    still bind to the page-level context. Side-panel mode wraps its own.
 */
import { Suspense, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Loader, Stack } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { useQueryClient } from '@tanstack/react-query'
import { formatHumanName } from '@medplum/core'
import type { Encounter, Patient } from '@medplum/fhirtypes'
import {
  fetchVisitPatientCoveragesNormalized,
  visitPatientCoverageQueryKey,
} from './hooks/usePatientCoverage'
import { useMiddlewareClient } from '@/hooks'
import { useMedplumPatient } from '@/hooks/useMedplumPatient'
import { logError } from '@/utils/errorUtils'
import { SCHEDULE_SLOT_MS, snapToScheduleGrid } from '@/utils/scheduleGrid'
import { resolveChartVisitContext } from '@/pages/Patient/components/progressNoteEncounter.utils'
import { ZoomPanel } from '@/components/scribe/ZoomPanel'
import type { DraftRecoveryRequest } from '@/components/scribe/DraftRecoveryNotice'
import { loadStoredScribeDraft } from './hooks/storedScribeDraft'
import { confirmLoadAiDraft, confirmRegenerateNote } from './hooks/confirmAiDraftActions'

/** Which "are you sure" gate each existing-note AI action must clear first. */
const RAIL_AI_CONFIRM = {
  load: confirmLoadAiDraft,
  regenerate: confirmRegenerateNote,
} as const
import { ScribeDraftGenerationError } from './hooks/scribeErrors'
import {
  readObservationsFromStorage,
  clearObservationsFromStorage,
} from '@/components/scribe/observationsStorage'
import { useFinalizeScribe } from './hooks/useFinalizeScribe'
import { applyDraftAndReveal, toStructuredDraft } from './hooks/applyDraftAndReveal'
import { useCallSession } from '@/components/session'
import { preferredScribeLanguage } from '@/components/scribe/encounterLanguage'
import type { LiveCaption } from './hooks/useLiveTranscription'
import { ScribeFormBridgeProvider, useScribeFormBridge } from './context/ScribeFormBridgeContext'
import { ProgressNoteEdit } from '@/pages/Patient/components/ProgressNoteEdit'
import { useIsManager } from '@/hooks/useIsManager'

/**
 * Loading skeleton shown during Suspense + while the side-panel mode resolves
 * the chart visit context. The dead "Open the Progress Note section…"
 * placeholder this used to ship next to was removed — the workspace now
 * mounts `<ProgressNoteEdit>` directly as the main content (empty/draft form
 * until the AI scribe finalizes or the doctor types).
 */
function ProgressNotePlaceholderSkeleton() {
  return (
    <Stack gap="sm" p="md" align="center">
      <Loader size="sm" />
    </Stack>
  )
}

/**
 * ZoomPanel width clamps per parent mode. Inlined here as plain constants
 * for Phase 2.4; item 2.7 (`<ZoomPanel>`) consumes them now via direct import.
 * Matches existing widths today:
 *   - `side-panel` → PatientSidePanel.tsx historical 550px width
 */
export const SIDE_PANEL_WIDTH = 550

const COVERAGE_STALE_MS = 1000 * 60 * 60

// Every manager-facing entry point to the Progress Note form shows this — the
// form never mounts for managers.
const MANAGER_NOTE_NOTICE = 'Progress notes for this visit are created by the treating physician.'

export type EncounterWorkspaceParentMode = 'page' | 'side-panel'

/**
 * Page-mode props — caller (CallPage) already resolved visit context.
 */
type PageModeProps = {
  parentMode: 'page'
  patientId: string
  encounterId?: string
  practitionerId: string
  practitionerDisplay: string
  organizationId: string
  projectId: string
  onFinish: () => void
  /**
   * Phase 3 item 3.9 — registered with `<ScribeFormBridgeContext>` so the
   * Create Progress Note flow can ask the page chrome to surface the SOAP
   * pane after the AI draft populates the form. Optional; the side-panel
   * mode has no chrome to swap so it skips this registration.
   */
  onShowSoapNote?: () => void
  /**
   * Suppress the new-encounter Progress Note edit form. Used by CallPage when
   * the URL holds a past-note `docRefId` segment (e.g.
   * `/call/:encounterId/:docRefId/progress-notes`) — `<Documents>` is already
   * rendering `<ProgressNoteViewer>` in the main content area, and showing the
   * edit form as a sibling causes the duplicate-render bug where the locked
   * past note and a fresh "New Progress Note" form appear side-by-side.
   *
   * `<ZoomPanel>` continues to render even when this is true so the active
   * Zoom session is not torn down while the doctor reviews a prior note.
   */
  hideEditForm?: boolean
  /**
   * Interrupted-visit recovery — prior transcript lines fetched from the
   * server, forwarded to `<ZoomPanel>` to seed the live transcript pane on a
   * Resume. Undefined on a normal fresh join.
   */
  recoveredCaptions?: LiveCaption[]
  /**
   * Interrupted-visit recovery — "Finish & generate note" request. The page
   * sets the provider scribe-session id to the recovered id and bumps `nonce`
   * to trigger the partial-finalize flow here (inside the form-bridge tree).
   * The flow finalizes with `partial: true`, populates + auto-submits the
   * Progress Note form, then routes to the saved draft — WITHOUT joining Zoom.
   * Null when no finish is requested.
   */
  finishRecovery?: { sessionId: string; nonce: number } | null
  /**
   * In-call edit→view toggle. Wired to `<ProgressNoteEdit onCancel>` so the
   * form's block-on-fail exit (`handleCancel` → awaited `flushPendingSave`,
   * blocking the transition on save failure) is REACHABLE in-call. Without
   * this the in-call form rendered no Cancel button, so the only exit was a
   * parent toggle / route-nav that unmounts the form and runs only the
   * non-awaitable best-effort flush — a failed final save would silently lose
   * edits and let Lock materialize stale content (#7). CallPage points this at
   * "hide the Progress Note pane" (the in-call edit→view transition).
   */
  onCancel?: () => void
  /**
   * Page-mode "Join Session" pre-join surface. Forwarded straight to
   * `<ZoomPanel showJoinPrompt onJoin>`. CallPage sets `showJoinPrompt` when the
   * doctor landed on `/call/:encounterId` without a live call yet (e.g. from the
   * schedule), and points `onJoin` at its single `requestJoin` entry. The
   * side-panel mode has no pre-join surface — it hands off to page mode.
   */
  showJoinPrompt?: boolean
  onJoin?: () => void
  /** Join-in-flight flag — `<ZoomPanel>` shows its "Connecting…" slot while set. */
  joinConnecting?: boolean
  /** PD-1105 — reopened encounter already has a Progress Note; forwarded to `<ZoomPanel>` to show the notice instead of the join slot. */
  showExistingNote?: boolean
  isExistingNoteLocked?: boolean
  onViewEditNote?: () => void
  onStartNewVisit?: () => void
  /** Which recovery the rail should offer; the action is attached here. */
  draftRecovery?: DraftRecoveryRequest | null
  /**
   * AI artifacts for an UNLOCKED existing note — lets the PD-1105 notice offer
   * "Load AI draft" / "Regenerate note" beside "Edit note". Null when the note
   * is locked or no artifact survives. The note still outranks both on
   * `resolveState`; these ids ride alongside for the explicit actions.
   */
  existingNoteAiArtifacts?: {
    draftDocRefId: string | null
    transcriptDocRefId: string | null
    transcriptUrl: string | null
  } | null
  /**
   * PD-1440 — the server refuses draft saves with a 409 once the visit has a
   * locked note. Forwarded to `<ProgressNoteEdit>` so a stale tab's rejected
   * autosave can tell CallPage to re-resolve the note state (which closes the
   * editor pane) instead of failing silently forever.
   */
  onSaveConflict?: () => void
  /**
   * PD-1378 — a recovery Retry was refused (a generation is already running);
   * the owner re-resolves the rail state so the card swaps to the status.
   */
  onDraftAlreadyGenerating?: () => void
}

/**
 * Side-panel-mode props — caller (patient-chart panel registry) only knows
 * the patient. The workspace resolves the chart visit context internally.
 */
type SidePanelModeProps = {
  parentMode: 'side-panel'
  patient: Patient
  onFinish: () => void
  /**
   * Optional escape hatch for the inline-in-chart flow. Forwarded to
   * `<ProgressNoteEdit>` so its top action bar surfaces a Cancel control.
   * The chart used to rely on the wrapping `<PatientSidePanel>`'s X button
   * to bail out of a new encounter; now that the workspace renders inline
   * as the chart's main content, the form itself owns the cancel UX.
   */
  onCancel?: () => void
}

export type EncounterWorkspaceProps = PageModeProps | SidePanelModeProps

export function EncounterWorkspace(props: EncounterWorkspaceProps) {
  // Phase 3 item 3.6/3.8/3.10 — the form, ZoomPanel/ScribeControls, and
  // suggestion modal are siblings that need to coordinate through a single
  // imperative bridge. Wrapping both modes in the provider keeps the wiring
  // identical between page and side-panel flows.
  if (props.parentMode === 'side-panel') {
    return (
      <ScribeFormBridgeProvider>
        <SidePanelWorkspace {...props} />
      </ScribeFormBridgeProvider>
    )
  }
  return (
    <ScribeFormBridgeProvider>
      <PageWorkspace {...props} />
    </ScribeFormBridgeProvider>
  )
}

function PageWorkspace({
  parentMode,
  patientId,
  encounterId,
  practitionerId,
  practitionerDisplay,
  organizationId,
  projectId,
  onFinish,
  onShowSoapNote,
  hideEditForm,
  recoveredCaptions,
  finishRecovery,
  onCancel,
  showJoinPrompt,
  onJoin,
  joinConnecting,
  showExistingNote,
  isExistingNoteLocked,
  onViewEditNote,
  onStartNewVisit,
  draftRecovery,
  existingNoteAiArtifacts,
  onSaveConflict,
  onDraftAlreadyGenerating,
}: PageModeProps) {
  // Resolve real display names for the ZoomPanel header/chip (§1/§3). Without
  // these the panel falls back to literal 'Patient' / 'PT' / 'DR'. The
  // provider name was already resolved upstream by CallPage
  // (`formatPractitionerDisplay(profile)` → e.g. "Wendolin Garcia, FNP") and
  // arrives as `practitionerDisplay`, so we forward it directly. The patient
  // name we read with the same TanStack-Query-cached hook the chart uses and
  // format with the app-wide `formatHumanName`. Both are resilient: while the
  // Patient is loading (or if it can't be resolved) `patientName` is undefined
  // and the ZoomPanel keeps its existing defaults. `/call/:encounterId` is the
  // telehealth experience, so the visit type is "Virtual Visit".
  const { data: patient } = useMedplumPatient(patientId)
  const patientName = patient?.name?.[0] ? formatHumanName(patient.name[0]) : undefined
  const providerName = practitionerDisplay || undefined
  // Managers (care coordinators) can't create/edit the scribe progress note.
  // While the role resolves, mount nothing — not the editor — so a manager
  // never flashes an editable form.
  const { isManager, loading: managerLoading } = useIsManager()

  // Phase 3 item 3.9 — register the chrome callback with the bridge. Held
  // in a ref so the registration effect only runs on mount/unmount; the
  // callback closes over the latest prop via the ref.
  const bridge = useScribeFormBridge()
  const showSoapNoteRef = useRef(onShowSoapNote)
  showSoapNoteRef.current = onShowSoapNote
  useEffect(() => {
    if (!bridge) return
    bridge.registerShowSoapNote(() => {
      showSoapNoteRef.current?.()
    })
    return () => {
      bridge.registerShowSoapNote(null)
    }
  }, [bridge])

  // Draft recovery — same reason this lives here rather than in CallPage: the
  // form bridge is provided below this component, so only code inside the tree
  // can reveal + auto-submit the note. CallPage decides WHICH recovery applies
  // and hands it down as `draftRecovery`; we supply the action.
  const medplum = useMedplum()
  const [recoveringDraft, setRecoveringDraft] = useState(false)
  // Ref, not state: a second click landing in the same render must see the guard
  // (same reason as `railAiActionInFlightRef` below). Separate ref from it —
  // sharing one would make that flow short-circuit its own call into here.
  const draftRecoveryInFlightRef = useRef(false)
  const runDraftRecovery = async (request: DraftRecoveryRequest): Promise<void> => {
    // CallPage only builds a recovery request for a resolved encounter, so this
    // is unreachable — the prop is optional for the side-panel mode.
    if (!encounterId) return
    // PD-1378 — the card is a status: the generation already running owns the
    // draft, and starting a second one is exactly what it exists to prevent.
    if (request.mode === 'generating') return
    // Managers never mount the Progress Note form — the bridge populate below
    // would wait forever on it. The rail already swaps their action for the notice.
    if (managerLoading) return
    if (isManager) {
      showNotification({ color: 'gray', message: MANAGER_NOTE_NOTICE })
      return
    }
    if (draftRecoveryInFlightRef.current) return
    draftRecoveryInFlightRef.current = true
    setRecoveringDraft(true)
    try {
      const result =
        request.mode === 'draft-ready'
          ? await loadStoredScribeDraft(medplum, request.draftDocRefId)
          : await generateFromTranscript(
              encounterId,
              request.transcriptDocRefId,
              // The scratchpad survives a failed generation (it's only cleared
              // once one succeeds), so the retry must feed the AI the same
              // observations the original attempt had.
              readObservationsFromStorage(encounterId),
            )
      // PD-1378 — a refusal is not a failure: another run owns the draft. The
      // hook already toasted; refresh the rail state so the card swaps to the
      // generating status instead of keeping a Retry that would 409 again.
      if (result.alreadyGenerating) {
        onDraftAlreadyGenerating?.()
        return
      }
      // An empty result means recovery FAILED. Revealing it would submit a blank
      // note — and because a note outranks a transcript on the rail, that would
      // permanently replace the retry with an empty document.
      if (!toStructuredDraft(result).hasAiSignal) {
        throw new Error('Draft recovery returned no content')
      }
      await applyDraftAndReveal(bridge, result, 'encounter-workspace')
      clearObservationsFromStorage(encounterId)
    } catch (err) {
      logError('encounter-workspace', 'Draft recovery failed', err)
      // `useFinalizeScribe` already toasts its own generation failures; only
      // surface the ones it can't have (reading back a stored draft, revealing).
      if (!(err instanceof ScribeDraftGenerationError)) {
        showNotification({
          color: 'red',
          title: 'Could not recover the note',
          message: 'The transcript is still saved. Try again, or write the note manually.',
        })
      }
    } finally {
      draftRecoveryInFlightRef.current = false
      setRecoveringDraft(false)
    }
  }

  // AI actions on the existing-note notice. Same recovery machinery as the
  // draft/transcript cards — but here a note EXISTS, so both actions replace
  // its content and confirm first (see confirmAiDraftActions). Managers keep
  // the plain notice: handlers stay undefined so the buttons never render.
  // `railAiAction` mirrors `recoveringDraft` but carries WHICH action is in
  // flight, so the notice can put the spinner on the button that owns it.
  const [railAiAction, setRailAiAction] = useState<'load' | 'regenerate' | null>(null)
  // Ref, not state: the guard must be visible to a second click that lands
  // while the first click's confirm modal is still open (same render).
  const railAiActionInFlightRef = useRef(false)
  const loadableDraftDocRefId =
    !managerLoading && !isManager ? (existingNoteAiArtifacts?.draftDocRefId ?? null) : null
  const regenerableTranscriptDocRefId =
    !managerLoading && !isManager ? (existingNoteAiArtifacts?.transcriptDocRefId ?? null) : null
  const runExistingNoteAiAction = async (
    kind: 'load' | 'regenerate',
    request: DraftRecoveryRequest,
  ): Promise<void> => {
    if (railAiActionInFlightRef.current) return
    railAiActionInFlightRef.current = true
    try {
      if (!(await RAIL_AI_CONFIRM[kind]())) return
      setRailAiAction(kind)
      await runDraftRecovery(request)
    } finally {
      railAiActionInFlightRef.current = false
      setRailAiAction(null)
    }
  }
  const onLoadAiDraft = loadableDraftDocRefId
    ? () =>
        void runExistingNoteAiAction('load', {
          mode: 'draft-ready',
          draftDocRefId: loadableDraftDocRefId,
          transcriptUrl: existingNoteAiArtifacts?.transcriptUrl ?? null,
        })
    : undefined
  const onRegenerateNote = regenerableTranscriptDocRefId
    ? () =>
        void runExistingNoteAiAction('regenerate', {
          mode: 'transcript-only',
          transcriptDocRefId: regenerableTranscriptDocRefId,
          transcriptUrl: existingNoteAiArtifacts?.transcriptUrl ?? null,
        })
    : undefined

  // Interrupted-visit recovery — "Finish & generate note" path. Runs HERE
  // (inside the form-bridge tree) rather than in CallPage so it can reuse the
  // exact populate + auto-submit + navigate sequence the ZoomPanel End-Session
  // flow uses. The page already adopted the recovered session id into provider
  // state (so `useFinalizeScribe` reads it), surfaced the Progress Note pane,
  // and bumped `finishRecovery.nonce`. We finalize with `partial: true`, push
  // the AI draft into the form, auto-submit, and route to the saved draft.
  // Crucially this does NOT join Zoom — the call is over.
  const { finalize, generateFromTranscript } = useFinalizeScribe()
  const { markSessionEnded } = useCallSession()
  const lastFinishNonceRef = useRef<number | null>(null)
  // Hold the mutable deps in refs so the effect can fire purely on nonce.
  const finishDepsRef = useRef({ finalize, bridge, markSessionEnded, patientId, encounterId })
  finishDepsRef.current = { finalize, bridge, markSessionEnded, patientId, encounterId }
  useEffect(() => {
    if (!finishRecovery) return
    if (lastFinishNonceRef.current === finishRecovery.nonce) return
    lastFinishNonceRef.current = finishRecovery.nonce

    const {
      finalize: doFinalize,
      bridge: b,
      markSessionEnded: endSession,
      patientId: pid,
      encounterId: encId,
    } = finishDepsRef.current

    void (async () => {
      try {
        const observations = readObservationsFromStorage(encId)
        // `partial: true` — interrupted visit; the AI prompt adds the
        // "extract only what's explicitly present" caveat.
        //
        // Pass the recovered `sessionId` EXPLICITLY (5th arg) rather than
        // relying on the provider's `scribeSessionId` having been seated by
        // CallPage's `setScribeSessionId(session.sessionId)`. CallPage calls
        // `markSessionActive` (a JOIN that resets `scribeSessionId: null`)
        // immediately before that setter, so without the explicit pass this
        // flow would race the null-then-set dispatch ordering and could
        // finalize against a null id. The id is already in
        // `finishRecovery.sessionId`, so we hand it straight through.
        const result = await doFinalize(
          'with-draft',
          pid,
          observations,
          true,
          finishRecovery.sessionId,
        )
        // Scratchpad consumed by the AI — clear it so it doesn't carry into the
        // next encounter.
        clearObservationsFromStorage(encId)
        await applyDraftAndReveal(b, result, 'encounter-workspace')
      } catch (err) {
        logError('encounter-workspace', 'Interrupted-visit finish flow failed', err)
      } finally {
        // We never joined Zoom on this path; just clear the provider's call
        // state so the panel + session tracking unwind cleanly.
        endSession()
      }
    })()
  }, [finishRecovery])

  return (
    <Suspense fallback={<ProgressNotePlaceholderSkeleton />}>
      {/*
        `data-parent-mode` parks the prop on the DOM so ZoomPanel siblings can
        infer their parent if needed.

        The Progress Note form mounts here and renders empty/draft, but DURING
        the call it is visually collapsed — CallPage keeps `showProgressNotePane`
        false, which flexes this pane to 0px (`overflow: hidden`, form still in
        the DOM so its RHF state is live). The doctor works in the `<ZoomPanel>`
        right rail, not in this pane. When the AI scribe finalize step pushes a
        populated draft through `<ScribeFormBridgeContext>.populateFromAi(...)`,
        the bridge's `showSoapNote()` → CallPage `onShowSoapNote` flips
        `showProgressNotePane` true, expanding this pane so the doctor can review
        and edit the draft.
      */}
      <div data-parent-mode={parentMode}>
        {hideEditForm || managerLoading ? null : isManager ? (
          <Alert color="gray" data-testid="progress-note-manager-notice" m="md">
            {MANAGER_NOTE_NOTICE}
          </Alert>
        ) : (
          <ProgressNoteEdit
            patientId={patientId}
            encounterId={encounterId}
            practitionerId={practitionerId}
            organizationId={organizationId}
            projectId={projectId}
            onFinish={onFinish}
            onCancel={onCancel}
            onSaveConflict={onSaveConflict}
            flushTop
          />
        )}
      </div>
      <ZoomPanel
        parentMode={parentMode}
        patientId={patientId}
        patientName={patientName}
        patientLanguage={patient ? preferredScribeLanguage(patient) : undefined}
        providerName={providerName}
        visitType="Virtual Visit"
        encounterId={encounterId}
        recoveredCaptions={recoveredCaptions}
        showJoinPrompt={showJoinPrompt}
        onJoin={onJoin}
        connecting={joinConnecting}
        showExistingNote={showExistingNote}
        isExistingNoteLocked={isExistingNoteLocked}
        onViewEditNote={onViewEditNote}
        onStartNewVisit={onStartNewVisit}
        draftRecovery={
          draftRecovery
            ? {
                mode: draftRecovery.mode,
                transcriptUrl: draftRecovery.transcriptUrl,
                onRejoin: draftRecovery.onRejoin,
                busy: recoveringDraft,
                onPrimary: () => void runDraftRecovery(draftRecovery),
                primaryUnavailableNotice:
                  !managerLoading && isManager ? MANAGER_NOTE_NOTICE : undefined,
              }
            : null
        }
        onLoadAiDraft={onLoadAiDraft}
        onRegenerateNote={onRegenerateNote}
        aiActionBusy={railAiAction}
        onWriteManualNote={isManager || managerLoading ? undefined : onShowSoapNote}
      />
    </Suspense>
  )
}

function SidePanelWorkspace({ patient }: SidePanelModeProps) {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const middleware = useMiddlewareClient()
  const navigate = useNavigate()

  // Thin entry-point: create the Encounter, then NAVIGATE to `/call/:encounterId`
  // with no `autoJoin` — so the doctor lands on the Join prompt, like a scheduled visit.
  const [error, setError] = useState<string>()

  // Read unstable props/state through refs so the bootstrap effect below
  // never re-fires when the parent re-renders — a re-fire mid-flight would
  // flip `active = false` and the in-flight `navigate('/call/<id>')` would be
  // silently skipped by the `if (!active) return` guard.
  const patientRef = useRef(patient)
  patientRef.current = patient

  // Guard so the bootstrap runs exactly once per mount even if React strict-
  // mode double-invokes effects.
  const hasRunRef = useRef(false)

  useEffect(() => {
    if (hasRunRef.current) return
    hasRunRef.current = true

    let active = true

    void (async () => {
      try {
        const currentPatient = patientRef.current
        const ctx = await resolveChartVisitContext(medplum, currentPatient)
        const patientId = currentPatient.id
        if (!active || !patientId) {
          return
        }

        // The middleware client surface is typed at the OpenAPI level; the
        // coverage helper expects a slimmer `{ get }`-only shape, so we cast
        // narrowly to avoid leaking the full ApiClient into that helper.
        const middlewareClient = middleware as unknown as {
          get: <T = unknown>(url: string) => Promise<{ result: T }>
        }

        await queryClient.prefetchQuery({
          queryKey: visitPatientCoverageQueryKey(patientId),
          staleTime: COVERAGE_STALE_MS,
          queryFn: () => fetchVisitPatientCoveragesNormalized(middlewareClient, patientId),
        })

        if (!active) return

        const subjectDisplay = currentPatient.name?.[0]
          ? formatHumanName(currentPatient.name[0])
          : undefined
        // Same shape as a booked visit's slot window: the schedule renders a
        // :00/:30 grid, and presetting BOTH period edges makes the
        // CallSessionProvider join/leave stamps no-op (they only write unset
        // edges), so an abandoned call can never leave an end-less Encounter
        // that crashes the unguarded SchedulePage.
        const start = snapToScheduleGrid()
        const createdEncounter = await medplum.createResource<Encounter>({
          resourceType: 'Encounter',
          status: 'planned',
          class: {
            system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
            code: 'VR',
            display: 'virtual',
          },
          subject: {
            reference: `Patient/${patientId}`,
            display: subjectDisplay,
          },
          participant: [
            {
              individual: {
                reference: `Practitioner/${ctx.practitionerId}`,
                display: ctx.practitionerDisplay || undefined,
              },
            },
          ],
          serviceProvider: {
            reference: `Organization/${ctx.organizationId}`,
          },
          period: {
            start: start.toISOString(),
            end: new Date(start.getTime() + SCHEDULE_SLOT_MS).toISOString(),
          },
          meta: {
            compartment: [
              { reference: `Patient/${patientId}` },
              { reference: `Project/${ctx.projectId}` },
            ],
          },
        })

        if (!active || !createdEncounter.id) return

        // Hand off to CallPage — no `autoJoin`. Deliberately NO `onFinish` here:
        // its newest-note re-select raced this navigate and swallowed the
        // hand-off whenever a notes list was mounted. Leaving the page IS the finish.
        navigate(`/call/${createdEncounter.id}`)
      } catch (e) {
        logError('encounter-workspace', 'Resolve chart visit context', e)
        if (active) {
          const message = e instanceof Error ? e.message : 'Failed to open progress note'
          setError(message)
          showNotification({
            color: 'red',
            title: 'Error',
            message,
          })
        }
      }
    })()

    return () => {
      active = false
    }
    // Refs (`onFinishRef`, `patientRef`) intentionally NOT in deps — they're
    // mutated above each render so the effect always sees fresh values
    // without re-firing. `medplum`/`middleware`/`queryClient`/`navigate` are
    // stable from their hook contracts.
  }, [medplum, middleware, navigate, queryClient])

  // Mirror the mockup's `goToNewEncounter` (`docs/ui-designs/06-telehealth-and-scribe.md`
  // and `ui-designs/.../ChartPage.tsx:14430`): opening the New Encounter panel
  // is the entry point that starts a real Zoom session and flips
  if (error) {
    return (
      <Alert color="red" title="Could not start encounter">
        {error}
      </Alert>
    )
  }

  if (!patient.id) {
    return (
      <Alert color="red" title="Could not load patient">
        Patient context is missing. Please refresh and try again.
      </Alert>
    )
  }

  // While the encounter is being created we just show a loader — the effect
  // above will navigate to `/call/:encounterId?autoJoin=1` as soon as the
  // Medplum write resolves, so this skeleton is never visible for long.
  return <ProgressNotePlaceholderSkeleton />
}
